import api from './api';
import type { AuthResponse, UpdateProfileData  } from '@/types';
// import { tokenManager } from './authApi';

export const userService = {
  // Login and fetch user data with tokens
  loginAndFetchUser: async (credentials: { email: string; password: string }): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', credentials);
    const responseData = response?.data as any;

    // Handle wrapped response structure: { status, message, data: AuthResponse }
    const authData = (responseData?.data && responseData?.status === 'success')
      ? responseData.data
      : responseData;

    if (!authData?.access_token || !authData?.user) {
      console.error('Invalid login response:', {
        responseData,
        authData,
        hasAccessToken: !!authData?.access_token,
        hasUser: !!authData?.user,
      });
      throw new Error('Invalid login response from server');
    }

    const authResponse: AuthResponse = {
      ...(authData as AuthResponse),
      status: responseData?.status ?? (authData as AuthResponse)?.status,
      message: responseData?.message ?? (authData as AuthResponse)?.message,
    };

    // Ensure expires_in is set (default to 30 minutes if missing)
    if (!authResponse.expires_in || typeof authResponse.expires_in !== 'number') {
      console.warn('expires_in missing or invalid in login response, defaulting to 1800 seconds', {
        expires_in: authResponse.expires_in,
        authData
      });
      authResponse.expires_in = 1800; // 30 minutes default
    }

    console.log('Login response prepared:', {
      hasAccessToken: !!authResponse.access_token,
      hasRefreshToken: !!authResponse.refresh_token,
      expiresIn: authResponse.expires_in,
      tokenType: authResponse.token_type
    });

    userService.updateTokens(authResponse);

    return authResponse;
  },

  // Get current user data
  getCurrentUser: async (): Promise<AuthResponse> => {
    const response = await api.get('/my-data');
    // Handle wrapped response structure: { status, message, data: AuthResponse }
    const responseData = response?.data as any;
    if (responseData?.data && responseData?.status === 'success') {
      return responseData.data as AuthResponse;
    }
    // Fallback to direct response (for backward compatibility)
    return responseData as AuthResponse;
  },

  // Update user profile
  updateProfile: async (data: UpdateProfileData): Promise<AuthResponse> => {
    const response = await api.put('/update-profile', data);
    // Handle wrapped response structure: { status, message, data: AuthResponse }
    const responseData = response.data as any;
    if (responseData?.data && responseData?.status === 'success') {
      return responseData.data as AuthResponse;
    }
    // Fallback to direct response (for backward compatibility)
    return responseData as AuthResponse;
  },

  // Update password
  updatePassword: async (data: {
    current_password: string;
    password: string;
    password_confirmation: string;
  }): Promise<AuthResponse> => {
    const response = await api.put('/update-password', data);
    // Handle wrapped response structure: { status, message, data: AuthResponse }
    const responseData = response.data as any;
    if (responseData?.data && responseData?.status === 'success') {
      return responseData.data as AuthResponse;
    }
    // Fallback to direct response (for backward compatibility)
    return responseData as AuthResponse;
  },

  // Upload profile image
  uploadProfileImage: async (file: File): Promise<AuthResponse> => {
    const formData = new FormData();
    formData.append('avatar', file);
    
    // Debug: Log FormData contents
    console.log('Uploading avatar:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      formDataKeys: Array.from(formData.keys()),
    });
    
    // Use POST request for file uploads (PUT doesn't work with multipart/form-data in PHP/Laravel)
    // Note: Don't set Content-Type header - axios will set it automatically with boundary
    const response = await api.post('/update-profile', formData);
    
    // Handle wrapped response structure: { status, message, data: AuthResponse }
    const responseData = response.data as any;
    if (responseData?.data && responseData?.status === 'success') {
      return responseData.data as AuthResponse;
    }
    // Fallback to direct response (for backward compatibility)
    return responseData as AuthResponse;
  },

  // Update tokens in localStorage
  updateTokens: (authResponse: AuthResponse) => {
    // Validate required fields
    if (!authResponse?.access_token) {
      console.error('updateTokens: Missing access_token', authResponse);
      return;
    }
    if (!authResponse?.refresh_token) {
      console.error('updateTokens: Missing refresh_token', authResponse);
      return;
    }

    // Calculate expiry time - expires_in is in seconds, convert to milliseconds
    // Default to 30 minutes (1800 seconds) if expires_in is missing or invalid
    const expiresInSeconds = authResponse.expires_in && typeof authResponse.expires_in === 'number' 
      ? authResponse.expires_in 
      : 1800; // Default 30 minutes
    
    const expiryTime = Date.now() + (expiresInSeconds * 1000);
    
    console.log('Storing tokens:', {
      hasAccessToken: !!authResponse.access_token,
      hasRefreshToken: !!authResponse.refresh_token,
      expiresIn: expiresInSeconds,
      expiryTime: new Date(expiryTime).toISOString()
    });

    localStorage.setItem('auth_token', authResponse.access_token);
    localStorage.setItem('refresh_token', authResponse.refresh_token);
    localStorage.setItem('token_expiry', expiryTime.toString());
    // Set refresh token timestamp for age validation
    localStorage.setItem('refresh_token_timestamp', Date.now().toString());
    // tokenManager.setTokens(authResponse);
  },

  // Clear tokens
  clearTokens: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('refresh_token_timestamp');
    localStorage.removeItem('last_refresh_attempt');
    // tokenManager.clearTokens();
  },

  // Helper to decode JWT token and get expiry from exp claim
  getTokenExpiry: (token: string): number | null => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
    } catch (e) {
      return null;
    }
  },

  isAuthenticated: () => {
    // return tokenManager.isAuthenticated();
    const token = localStorage.getItem('auth_token');
    if (!token) return false;
    
    // Try to get expiry from token itself first (more accurate)
    const tokenExpiry = userService.getTokenExpiry(token);
    if (tokenExpiry) {
      // Check if token is expired (with 5 minute buffer)
      return Date.now() < (tokenExpiry - (5 * 60 * 1000));
    }
    
    // Fallback to stored expiry
    const expiry = localStorage.getItem('token_expiry');
    if (!expiry) return false;
    
    // Check if token is expired (with 5 minute buffer)
    return Date.now() < (parseInt(expiry) - (5 * 60 * 1000));
  },

  // Logout - call backend logout endpoint
  logout: async (): Promise<void> => {
    try {
      console.error('Logging out user...');
      await api.post('/logout');
    } catch (error) {
      // Even if logout fails on backend, clear tokens locally
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear tokens locally
      // localStorage.removeItem('auth_token');
      // localStorage.removeItem('refresh_token');
      userService.clearTokens();
    }
  }
};